// --- Our Modules --------------------------------------------------------------------------------
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyIconName } from "@/UI/atoms/Icon/types";
// --- Logic --------------------------------------------------------------------------------------
export type TyNavTab = { path: string; model: string; label: string; icon: TyIconName };

/**
 * The dashboard's destinations, already filtered by role. The sidebar, the phone bar and the "more"
 * panel all read this one list, so a role can never show a tab on one surface and hide it on another.
 *
 * Settings is not here: it lives in the profile popup beside the account's own actions.
 *
 * `footerTabs` is the second group — the housekeeping that sits at the bottom of the sidebar rather
 * than in the run of daily sections.
 */
export default function useNavTabs(): { tabs: TyNavTab[]; footerTabs: TyNavTab[]; settingsTab: TyNavTab | null } {
    const t = useTranslation();
    const { canAccess } = usePermission();

    const tabs: TyNavTab[] = [
        { path: "/", model: "home", label: t.sideBar.home, icon: "home" },
        { path: "/properties", model: "properties", label: t.sideBar.properties, icon: "properties" },
        { path: "/crm", model: "crm", label: t.sideBar.crm, icon: "crm" },
        { path: "/drive", model: "drive", label: t.sideBar.drive, icon: "drive" },
        { path: "/team", model: "team", label: t.sideBar.team, icon: "team" },
        { path: "/subscription", model: "subscription", label: t.sideBar.subscription, icon: "subscription" },
    ];

    /**
     * Housekeeping rather than work: pinned to the foot of the sidebar, below the sections somebody
     * opens all day. The trash is somewhere you go when something went wrong, not a destination.
     */
    const footerTabs: TyNavTab[] = [
        /** Every role reaches it — a member hits problems too, and sees their own requests only. */
        { path: "/support", model: "support", label: t.sideBar.support, icon: "support" },
        { path: "/trash", model: "trash", label: t.sideBar.trash, icon: "trash" },
    ];

    const settings: TyNavTab = { path: "/settings", model: "settings", label: t.sideBar.settings, icon: "settings" };

    return {
        tabs: tabs.filter((tab) => canAccess(tab.model)),
        footerTabs: footerTabs.filter((tab) => canAccess(tab.model)),
        settingsTab: canAccess(settings.model) ? settings : null,
    };
}
